import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Autocomplete, Box, Grid, MenuItem, TextField } from "@mui/material";
import { useTranslate } from "react-admin";
import {
  COMMON_TIMEZONES,
  INTERVAL_UNIT_CHOICES,
  SCHEDULE_TYPE_CHOICES,
} from "../../constants";

export const SchedulingSection: React.FC = () => {
  const translate = useTranslate();
  const { control } = useFormContext();
  const scheduleType = useWatch({ control, name: "schedule.type" });

  return (
    <>
      <Controller
        name="schedule.type"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            select
            fullWidth
            sx={{ mb: 2 }}
            label={translate("resources.test.fields.scheduleType")}
            InputLabelProps={{ shrink: true }}
          >
            {SCHEDULE_TYPE_CHOICES.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {translate(c.labelKey)}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      {scheduleType !== "NONE" && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "12px",
            p: { xs: 1.5, sm: 2 },
            mb: 2,
          }}
        >
          <Grid container spacing={{ xs: 1.5, sm: 3 }}>
            <Grid item xs={12} md={6}>
              <Controller
                name="schedule.date"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    fullWidth
                    value={field.value ?? ""}
                    label={translate("resources.test.fields.scheduleDate")}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: new Date().toISOString().slice(0, 10),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="schedule.time"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="time"
                    fullWidth
                    label={translate("resources.test.fields.scheduleTime")}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            {scheduleType === "RECURRING" && (
              <>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="schedule.recurring.intervalValue"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        fullWidth
                        label={translate("resources.test.fields.intervalValue")}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="schedule.recurring.intervalUnit"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        label={translate("resources.test.fields.intervalUnit")}
                        InputLabelProps={{ shrink: true }}
                      >
                        {INTERVAL_UNIT_CHOICES.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {translate(c.labelKey)}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="schedule.recurring.expiresDate"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="date"
                        fullWidth
                        value={field.value ?? ""}
                        label={translate(
                          "resources.test.fields.expirationDate",
                        )}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: new Date().toISOString().slice(0, 10),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="schedule.recurring.expiresTime"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="time"
                        fullWidth
                        label={translate(
                          "resources.test.fields.expirationTime",
                        )}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Controller
                name="schedule.timezone"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={COMMON_TIMEZONES}
                    value={field.value}
                    onChange={(_, v) => field.onChange(v ?? "")}
                    onInputChange={(_, v) => field.onChange(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label={translate("resources.test.fields.timezone")}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};

export default SchedulingSection;
